import actions from './actions';
import authActions from '../auth/actions';

const initState = {
  loading: false,
  loaded: false,
  profile: null,
  profileId: null,
  invoices: [],
  error: null,
  activating: false,
  activateError: null,
  creating: false,
  createError: null,
  // admin
  adminSearch: {
    loading: false,
    results: [],
    error: null,
  },
  adminPayer: {
    payerUid: null,
    loading: false,
    profiles: [],
    payerInfo: null,
    error: null,
  },
  adminActioning: false,
  adminActionError: null,
  adminAsaasSubs: {
    loading: false,
    results: [],
    totalCount: 0,
    hasMore: false,
    offset: 0,
    limit: 20,
    filters: { status: null, customer: null },
    hadCustomerFallback: false,
    error: null,
  },
  adminPayerHps: {
    loading: false,
    payerUid: null,
    masterUid: null,
    options: [],
    members: [],
    error: null,
  },
  // Contagens dos tiles da fila de trabalho. `unlinked` vem do job noturno
  // (config/billingQueueStats), o resto é contagem no servidor.
  adminQueueCounts: {
    loading: false,
    counts: {
      trial: null, pending: null, overdue: null, value: null, active: null,
    },
    exceptions: { reconciliation: null },
    unlinked: { count: null, computedAt: null },
    fetchedAt: null,
    error: null,
  },
  adminQueue: {
    queue: null,
    status: null,
    loading: false,
    rows: [],
    hasMore: false,
    cursor: null,
    error: null,
  },
  // payerUid → { loading, found, subscription, customer, error } na conta antiga
  adminLegacyMatch: { byPayer: {}, configured: true },
  adminLegacyLookup: {
    payerUid: null, query: '', loading: false, data: null, error: null,
  },
  adminPreviewMigration: {
    loading: false, data: null, error: null,
  },
  adminProfileHistory: {
    profileId: null,
    loading: false,
    invoices: [],
    changeEvents: [],
    courtesies: [],
    proRataMerges: [],
    error: null,
  },
  adminMigrationResult: null,
  adminAsaasCustomersSearch: {
    loading: false,
    query: '',
    results: [],
    error: null,
  },
  adminUnlinked: {
    loading: false,
    results: [],
    hasMore: false,
    nextCursor: null,
    term: '',
    onlyMasters: true,
    error: null,
  },
  // Mapa profileId → { loading, payment, error } pra fatura PENDING atual
  // buscada do Asaas em tempo real (sem cache do Firestore).
  adminCurrentInvoices: {},
};

export default function meagendaBillingReducer(state = initState, action) {
  switch (action.type) {
    case authActions.CLEAR_STATES:
      return { ...initState };

    case actions.LOAD_BILLING_PROFILE_REQUEST:
      return {
        ...state, loading: true, error: null,
      };
    case actions.BILLING_PROFILE_UPDATED:
      return {
        ...state,
        loading: false,
        loaded: true,
        // snapshot ok limpa erro transitório anterior do listener
        error: null,
        profile: action.payload.profile,
        profileId: action.payload.profileId,
        // Troca de profile (admin moveu uid, secretária herda master): limpa as
        // invoices da sessão anterior até o novo snapshot de invoices chegar.
        invoices: action.payload.profileId !== state.profileId ? [] : state.invoices,
      };
    case actions.BILLING_PROFILE_NOT_FOUND:
      return {
        ...state,
        loading: false,
        loaded: true,
        error: null,
        profile: null,
        profileId: null,
        invoices: [],
      };
    case actions.BILLING_PROFILE_ERROR:
      return {
        ...state, loading: false, error: action.payload,
      };
    case actions.BILLING_INVOICES_UPDATED:
      return { ...state, invoices: action.payload };

    case actions.ACTIVATE_TRIAL_REQUEST:
      return { ...state, activating: true, activateError: null };
    case actions.ACTIVATE_TRIAL_SUCCESS:
      return { ...state, activating: false };
    case actions.ACTIVATE_TRIAL_ERROR:
      return { ...state, activating: false, activateError: action.payload };

    case actions.CREATE_PROFILE_REQUEST:
      return { ...state, creating: true, createError: null };
    case actions.CREATE_PROFILE_SUCCESS:
      return { ...state, creating: false };
    case actions.CREATE_PROFILE_ERROR:
      return { ...state, creating: false, createError: action.payload };

    // Admin search
    case actions.ADMIN_SEARCH_PROFILES_REQUEST:
      return {
        ...state,
        adminSearch: {
          ...state.adminSearch, loading: true, error: null,
        },
      };
    case actions.ADMIN_SEARCH_PROFILES_SUCCESS:
      return {
        ...state,
        adminSearch: { loading: false, results: action.payload, error: null },
      };
    case actions.ADMIN_SEARCH_PROFILES_ERROR:
      return {
        ...state,
        adminSearch: { ...state.adminSearch, loading: false, error: action.payload },
      };
    case actions.ADMIN_CLEAR_SEARCH:
      return {
        ...state,
        adminSearch: { loading: false, results: [], error: null },
      };

    // Admin profiles by payer
    case actions.ADMIN_LOAD_PROFILES_BY_PAYER_REQUEST:
      return {
        ...state,
        adminPayer: {
          payerUid: action.payload.payerUid,
          loading: true,
          profiles: [],
          payerInfo: null,
          error: null,
        },
      };
    case actions.ADMIN_LOAD_PROFILES_BY_PAYER_SUCCESS:
      return {
        ...state,
        adminPayer: {
          ...state.adminPayer,
          loading: false,
          profiles: action.payload.profiles,
          payerInfo: action.payload.payerInfo || null,
          error: null,
        },
      };
    case actions.ADMIN_LOAD_PROFILES_BY_PAYER_ERROR:
      return {
        ...state,
        adminPayer: { ...state.adminPayer, loading: false, error: action.payload },
      };
    case actions.ADMIN_CLEAR_PAYER:
      return {
        ...state,
        adminPayer: {
          payerUid: null, loading: false, profiles: [], error: null,
        },
        adminCurrentInvoices: {},
      };

    case actions.ADMIN_LOAD_CURRENT_INVOICE_REQUEST:
      return {
        ...state,
        adminCurrentInvoices: {
          ...state.adminCurrentInvoices,
          [action.payload.profileId]: {
            ...(state.adminCurrentInvoices[action.payload.profileId] || {}),
            loading: true,
            error: null,
          },
        },
      };
    case actions.ADMIN_LOAD_CURRENT_INVOICE_SUCCESS:
      return {
        ...state,
        adminCurrentInvoices: {
          ...state.adminCurrentInvoices,
          [action.payload.profileId]: {
            loading: false,
            payment: action.payload.payment,
            pendingPayments: action.payload.pendingPayments || [],
            error: null,
            fetchedAt: Date.now(),
          },
        },
      };
    case actions.ADMIN_LOAD_CURRENT_INVOICE_ERROR:
      return {
        ...state,
        adminCurrentInvoices: {
          ...state.adminCurrentInvoices,
          [action.payload.profileId]: {
            ...(state.adminCurrentInvoices[action.payload.profileId] || {}),
            loading: false,
            error: action.payload.error,
          },
        },
      };
    case actions.ADMIN_CLEAR_CURRENT_INVOICES:
      return { ...state, adminCurrentInvoices: {} };

    // Fila de trabalho
    case actions.ADMIN_LOAD_QUEUE_COUNTS_REQUEST:
      return {
        ...state,
        adminQueueCounts: { ...state.adminQueueCounts, loading: true, error: null },
      };
    case actions.ADMIN_LOAD_QUEUE_COUNTS_SUCCESS:
      return {
        ...state,
        adminQueueCounts: {
          loading: false,
          counts: action.payload.counts,
          exceptions: action.payload.exceptions,
          unlinked: action.payload.unlinked,
          fetchedAt: Date.now(),
          error: null,
        },
      };
    case actions.ADMIN_LOAD_QUEUE_COUNTS_ERROR:
      return {
        ...state,
        adminQueueCounts: { ...state.adminQueueCounts, loading: false, error: action.payload },
      };

    case actions.ADMIN_LOAD_QUEUE_REQUEST:
      return {
        ...state,
        adminQueue: {
          ...state.adminQueue,
          queue: action.payload.queue,
          status: action.payload.status,
          loading: true,
          error: null,
          rows: action.payload.cursor ? state.adminQueue.rows : [],
        },
      };
    case actions.ADMIN_LOAD_QUEUE_SUCCESS:
      return {
        ...state,
        adminQueue: {
          queue: action.payload.queue,
          status: action.payload.status,
          loading: false,
          rows: action.payload.append
            ? [...state.adminQueue.rows, ...action.payload.rows]
            : action.payload.rows,
          hasMore: action.payload.hasMore,
          cursor: action.payload.cursor,
          error: null,
        },
      };
    case actions.ADMIN_LOAD_QUEUE_ERROR:
      return {
        ...state,
        adminQueue: { ...state.adminQueue, loading: false, error: action.payload },
      };
    case actions.ADMIN_CLEAR_QUEUE:
      return { ...state, adminQueue: { ...initState.adminQueue } };

    case actions.ADMIN_LEGACY_MATCH_REQUEST: {
      const byPayer = { ...state.adminLegacyMatch.byPayer };
      action.payload.payerUids.forEach((uid) => {
        byPayer[uid] = { loading: true, error: null };
      });
      return { ...state, adminLegacyMatch: { ...state.adminLegacyMatch, byPayer } };
    }
    case actions.ADMIN_LEGACY_MATCH_SUCCESS: {
      const byPayer = { ...state.adminLegacyMatch.byPayer };
      action.payload.payerUids.forEach((uid) => {
        const result = action.payload.results[uid];
        byPayer[uid] = {
          loading: false,
          found: !!(result && result.found),
          code: result?.code || null,
          reason: result?.reason || null,
          searchedBy: result?.searchedBy || null,
          customer: result?.customer || null,
          subscription: result?.subscription || null,
          subscriptionCount: result?.subscriptionCount || 0,
          error: action.payload.errors[uid] || null,
        };
      });
      return {
        ...state,
        adminLegacyMatch: { byPayer, configured: action.payload.configured },
      };
    }
    case actions.ADMIN_LEGACY_MATCH_ERROR: {
      const byPayer = { ...state.adminLegacyMatch.byPayer };
      action.payload.payerUids.forEach((uid) => {
        byPayer[uid] = { loading: false, found: false, error: action.payload.error };
      });
      return { ...state, adminLegacyMatch: { ...state.adminLegacyMatch, byPayer } };
    }

    // Nova busca do mesmo pagador (termo digitado) mantém a lista anterior
    // na tela até a resposta chegar.
    case actions.ADMIN_LEGACY_LOOKUP_REQUEST:
      return {
        ...state,
        adminLegacyLookup: {
          payerUid: action.payload.payerUid,
          query: action.payload.query || '',
          loading: true,
          data: state.adminLegacyLookup.payerUid === action.payload.payerUid
            ? state.adminLegacyLookup.data
            : null,
          error: null,
        },
      };
    case actions.ADMIN_LEGACY_LOOKUP_SUCCESS:
      return {
        ...state,
        adminLegacyLookup: {
          ...state.adminLegacyLookup,
          payerUid: action.payload.payerUid,
          loading: false,
          data: action.payload.data,
          error: null,
        },
      };
    case actions.ADMIN_LEGACY_LOOKUP_ERROR:
      return {
        ...state,
        adminLegacyLookup: {
          ...state.adminLegacyLookup,
          payerUid: action.payload.payerUid,
          loading: false,
          data: null,
          error: action.payload.error,
        },
      };

    case actions.ADMIN_PREVIEW_MIGRATION_REQUEST:
      return {
        ...state,
        adminPreviewMigration: { ...state.adminPreviewMigration, loading: true, error: null },
      };
    case actions.ADMIN_PREVIEW_MIGRATION_SUCCESS:
      return {
        ...state,
        adminPreviewMigration: { loading: false, data: action.payload, error: null },
      };
    case actions.ADMIN_PREVIEW_MIGRATION_ERROR:
      return {
        ...state,
        adminPreviewMigration: { loading: false, data: null, error: action.payload },
      };
    case actions.ADMIN_CLEAR_PREVIEW_MIGRATION:
      return { ...state, adminPreviewMigration: { ...initState.adminPreviewMigration } };

    case actions.ADMIN_CLEAR_MIGRATION_RESULT:
      return { ...state, adminMigrationResult: null };

    case actions.ADMIN_LOAD_PROFILE_HISTORY_REQUEST:
      return {
        ...state,
        adminProfileHistory: {
          ...initState.adminProfileHistory,
          profileId: action.payload.profileId,
          loading: true,
        },
      };
    case actions.ADMIN_LOAD_PROFILE_HISTORY_SUCCESS:
      return {
        ...state,
        adminProfileHistory: {
          profileId: action.payload.profileId,
          loading: false,
          invoices: action.payload.invoices,
          changeEvents: action.payload.changeEvents,
          courtesies: action.payload.courtesies,
          proRataMerges: action.payload.proRataMerges || [],
          error: null,
        },
      };
    case actions.ADMIN_LOAD_PROFILE_HISTORY_ERROR:
      return {
        ...state,
        adminProfileHistory: {
          ...state.adminProfileHistory,
          loading: false,
          error: action.payload.error,
        },
      };

    // Admin actions (link / cancel / move / setCovered / recompute / setMode / prorata)
    case actions.ADMIN_LINK_PROFILE_REQUEST:
    case actions.ADMIN_CANCEL_PROFILE_REQUEST:
    case actions.ADMIN_SUSPEND_PROFILE_REQUEST:
    case actions.ADMIN_REACTIVATE_PROFILE_REQUEST:
    case actions.ADMIN_MOVE_UID_REQUEST:
    case actions.ADMIN_SET_COVERED_UIDS_REQUEST:
    case actions.ADMIN_RECOMPUTE_PROFILE_REQUEST:
    case actions.ADMIN_SET_PROFILE_MODE_REQUEST:
    case actions.ADMIN_SET_TRIAL_REQUEST:
    case actions.ADMIN_FORCE_PRORATA_MERGE_REQUEST:
    case actions.ADMIN_FORGIVE_PRORATA_REQUEST:
    case actions.ADMIN_APPLY_COURTESY_REQUEST:
    case actions.ADMIN_MIGRATE_PROFILE_REQUEST:
      return { ...state, adminActioning: true, adminActionError: null };
    case actions.ADMIN_LINK_PROFILE_SUCCESS:
      return {
        ...state,
        adminActioning: false,
        adminMigrationResult: action.payload || null,
      };
    case actions.ADMIN_CANCEL_PROFILE_SUCCESS:
    case actions.ADMIN_SUSPEND_PROFILE_SUCCESS:
    case actions.ADMIN_REACTIVATE_PROFILE_SUCCESS:
    case actions.ADMIN_MOVE_UID_SUCCESS:
    case actions.ADMIN_SET_COVERED_UIDS_SUCCESS:
    case actions.ADMIN_RECOMPUTE_PROFILE_SUCCESS:
    case actions.ADMIN_SET_PROFILE_MODE_SUCCESS:
    case actions.ADMIN_SET_TRIAL_SUCCESS:
    case actions.ADMIN_FORCE_PRORATA_MERGE_SUCCESS:
    case actions.ADMIN_FORGIVE_PRORATA_SUCCESS:
    case actions.ADMIN_APPLY_COURTESY_SUCCESS:
      return { ...state, adminActioning: false };
    case actions.ADMIN_LINK_PROFILE_ERROR:
    case actions.ADMIN_CANCEL_PROFILE_ERROR:
    case actions.ADMIN_SUSPEND_PROFILE_ERROR:
    case actions.ADMIN_REACTIVATE_PROFILE_ERROR:
    case actions.ADMIN_MOVE_UID_ERROR:
    case actions.ADMIN_SET_COVERED_UIDS_ERROR:
    case actions.ADMIN_RECOMPUTE_PROFILE_ERROR:
    case actions.ADMIN_SET_PROFILE_MODE_ERROR:
    case actions.ADMIN_SET_TRIAL_ERROR:
    case actions.ADMIN_FORCE_PRORATA_MERGE_ERROR:
    case actions.ADMIN_FORGIVE_PRORATA_ERROR:
    case actions.ADMIN_APPLY_COURTESY_ERROR:
      return { ...state, adminActioning: false, adminActionError: action.payload };
    case actions.ADMIN_MIGRATE_PROFILE_SUCCESS:
      return { ...state, adminActioning: false, adminMigrationResult: action.payload };
    case actions.ADMIN_MIGRATE_PROFILE_ERROR:
      return {
        ...state,
        adminActioning: false,
        adminActionError: action.payload.error,
        adminMigrationResult: null,
      };

    case actions.ADMIN_LIST_ASAAS_SUBS_REQUEST:
      return {
        ...state,
        adminAsaasSubs: {
          ...state.adminAsaasSubs,
          loading: true,
          error: null,
        },
      };
    case actions.ADMIN_LIST_ASAAS_SUBS_SUCCESS:
      return {
        ...state,
        adminAsaasSubs: {
          loading: false,
          results: action.payload.results,
          totalCount: action.payload.totalCount,
          hasMore: action.payload.hasMore,
          offset: action.payload.offset,
          limit: action.payload.limit,
          filters: action.payload.filters,
          hadCustomerFallback: action.payload.hadCustomerFallback,
          error: null,
        },
      };
    case actions.ADMIN_LIST_ASAAS_SUBS_ERROR:
      return {
        ...state,
        adminAsaasSubs: {
          ...state.adminAsaasSubs,
          loading: false,
          error: action.payload,
        },
      };
    case actions.ADMIN_LIST_ASAAS_SUBS_CLEAR:
      return {
        ...state,
        adminAsaasSubs: {
          loading: false,
          results: [],
          totalCount: 0,
          hasMore: false,
          offset: 0,
          limit: 20,
          filters: { status: null, customer: null },
          hadCustomerFallback: false,
          error: null,
        },
      };

    case actions.ADMIN_LOAD_PAYER_HPS_REQUEST:
      return {
        ...state,
        adminPayerHps: {
          ...initState.adminPayerHps,
          loading: true,
          payerUid: action.payload.payerUid,
        },
      };
    case actions.ADMIN_LOAD_PAYER_HPS_SUCCESS:
      return {
        ...state,
        adminPayerHps: {
          ...state.adminPayerHps,
          loading: false,
          masterUid: action.payload.masterUid || null,
          options: action.payload.options,
          members: action.payload.members || [],
          error: null,
        },
      };
    case actions.ADMIN_LOAD_PAYER_HPS_ERROR:
      return {
        ...state,
        adminPayerHps: {
          ...state.adminPayerHps,
          loading: false,
          error: action.payload,
        },
      };
    case actions.ADMIN_CLEAR_PAYER_HPS:
      return {
        ...state,
        adminPayerHps: { ...initState.adminPayerHps },
      };

    case actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_REQUEST:
      return {
        ...state,
        adminAsaasCustomersSearch: {
          ...state.adminAsaasCustomersSearch,
          loading: true,
          query: action.payload.query,
          error: null,
        },
      };
    case actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_SUCCESS:
      return {
        ...state,
        adminAsaasCustomersSearch: {
          ...state.adminAsaasCustomersSearch,
          loading: false,
          results: action.payload.results,
          error: null,
        },
      };
    case actions.ADMIN_SEARCH_ASAAS_CUSTOMERS_ERROR:
      return {
        ...state,
        adminAsaasCustomersSearch: {
          ...state.adminAsaasCustomersSearch,
          loading: false,
          error: action.payload,
        },
      };
    case actions.ADMIN_CLEAR_ASAAS_CUSTOMERS_SEARCH:
      return {
        ...state,
        adminAsaasCustomersSearch: {
          loading: false, query: '', results: [], error: null,
        },
      };

    case actions.ADMIN_LIST_UNLINKED_REQUEST: {
      const isReset = !!action.payload?.reset;
      return {
        ...state,
        adminUnlinked: {
          ...state.adminUnlinked,
          loading: true,
          error: null,
          ...(isReset
            ? {
              results: [],
              nextCursor: null,
              hasMore: false,
              term: action.payload?.term || '',
              onlyMasters: action.payload?.onlyMasters !== false,
            }
            : {}),
        },
      };
    }
    case actions.ADMIN_LIST_UNLINKED_SUCCESS: {
      const {
        results,
        hasMore,
        nextCursor,
        append,
      } = action.payload;
      return {
        ...state,
        adminUnlinked: {
          ...state.adminUnlinked,
          loading: false,
          results: append
            ? [...state.adminUnlinked.results, ...results]
            : results,
          hasMore,
          nextCursor,
          error: null,
        },
      };
    }
    case actions.ADMIN_LIST_UNLINKED_ERROR:
      return {
        ...state,
        adminUnlinked: {
          ...state.adminUnlinked,
          loading: false,
          error: action.payload,
        },
      };
    case actions.ADMIN_LIST_UNLINKED_CLEAR:
      return {
        ...state,
        adminUnlinked: {
          loading: false,
          results: [],
          hasMore: false,
          nextCursor: null,
          term: '',
          onlyMasters: true,
          error: null,
        },
      };

    default:
      return state;
  }
}
